import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';
import {
  FiAlertCircle,
  FiArrowLeft,
  FiBriefcase,
  FiCheckCircle,
  FiChevronRight,
  FiClock,
  FiEdit2,
  FiEye,
  FiHash,
  FiHome,
  FiInbox,
  FiLayers,
  FiMapPin,
  FiPlus,
  FiTrash2,
} from 'react-icons/fi';

import BasePage from '../../../../components/BasePage';
import LoadingState from '../../../../components/LoadingState';
import api from '../../../../services/api';
import { useAuth } from '../../../../hooks/auth';
import { useToast } from '../../../../hooks/useToast';

import {
  Avatar,
  BackLink,
  Card,
  CityItem,
  CityList,
  ComplaintItem,
  ComplaintList,
  DangerButton,
  EmptyState,
  Field,
  FieldGroup,
  FormField,
  GhostButton,
  HeaderActions,
  Identity,
  InfoGrid,
  ModalForm,
  ModalNote,
  Page,
  PrimaryButton,
  ProfileHeader,
  SectionBody,
  SectionHeader,
  SectionLabel,
  SmallAction,
  StatCard,
  StatsRow,
  StatusChip,
  TabButton,
  Tabs,
} from './styles';

interface ICityRef {
  nome?: string;
  uf?: string;
}

interface ICityInfo {
  id: string;
  name: string;
  address?: string;
  address2?: string;
  city_id?: number;
  state?: string;
  postal_code?: string;
  company_id?: string;
  city?: ICityRef;
}

interface IComplaintInfo {
  id: string;
  protocol?: string;
  status?: number;
  priority?: number;
  is_archived?: boolean;
  created_at?: string;
}

interface ISyndicateInfo {
  id: string;
  nome_fantasia: string;
}

interface IIBGEState {
  uf: string;
  nome: string;
}

interface IIBGECity {
  id: number;
  nome: string;
}

interface ICompanyInfo {
  id: string;
  nome_fantasia: string;
  razao_social?: string;
  cnpj?: string;
  syndicate_id?: string;
  syndicate?: ISyndicateInfo;
  address?: string;
  address2?: string;
  city_id?: number;
  state?: string;
  postal_code?: string;
  cities?: ICityInfo[];
  complaints?: IComplaintInfo[];
  city?: ICityRef;
}

const STATUS_LABEL: Record<number, string> = {
  1: 'Recebido',
  2: 'Em análise',
  3: 'Finalizado',
  4: 'Arquivado',
};
const STATUS_TONE: Record<number, 'warning' | 'info' | 'success' | 'neutral'> =
  {
    1: 'warning',
    2: 'info',
    3: 'success',
    4: 'neutral',
  };

const cnpjMask = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
};

const cepMask = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits.replace(/(\d{5})(\d)/, '$1-$2');
};

const initials = (name: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts
    .map(p => p[0])
    .join('')
    .toUpperCase();
};

const renderValue = (value: string | undefined | null): JSX.Element => {
  if (!value) return <span className="value empty">Não informado</span>;
  return <span className="value">{value}</span>;
};

const companySchema = Yup.object().shape({
  nome_fantasia: Yup.string().required('Nome fantasia é obrigatório'),
});

const cityFormDefaults = (id: string) => ({
  name: '',
  postal_code: '',
  address: '',
  address2: '',
  state: '',
  city_id: '',
  company_id: id,
});

interface ICityFormValues {
  name: string;
  postal_code: string;
  address: string;
  address2: string;
  state: string;
  city_id: string | number;
  company_id: string;
}

interface ICompanyFormValues {
  nome_fantasia: string;
  razao_social: string;
  cnpj: string;
  syndicate_id: string;
  postal_code: string;
  address: string;
  address2: string;
  state: string;
  city_id: string | number;
}

type TabKey = 'overview' | 'complaints' | 'cities';

const ShowCompany: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { user } = useAuth();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [companyInfo, setCompanyInfo] = useState<ICompanyInfo>();
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  const [syndicates, setSyndicates] = useState<ISyndicateInfo[]>([]);
  const [states, setStates] = useState<IIBGEState[]>([]);
  const [cities, setCities] = useState<IIBGECity[]>([]);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [showAddCityModal, setShowAddCityModal] = useState(false);
  const [showEditCityModal, setShowEditCityModal] = useState(false);
  const [showDeleteCityModal, setShowDeleteCityModal] = useState(false);
  const [editingCity, setEditingCity] = useState<ICityInfo | null>(null);
  const [pendingCityDeleteId, setPendingCityDeleteId] = useState<string | null>(
    null,
  );

  const [submitting, setSubmitting] = useState(false);

  const loadCompany = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<ICompanyInfo>(`/companies/${id}`);
      setCompanyInfo(response.data);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadCompany();
  }, [loadCompany]);

  useEffect(() => {
    if (!companyInfo) return;
    document.title = `${companyInfo.nome_fantasia} · Empresas - Viva Voz`;
  }, [companyInfo]);

  const handleLoadStates = useCallback(async () => {
    if (states.length > 0) return;
    const response = await api.get<IIBGEState[]>('/ibge/states');
    setStates(response.data);
  }, [states.length]);

  const handleLoadCities = useCallback(async (uf: string) => {
    if (!uf) {
      setCities([]);
      return;
    }
    const response = await api.get<IIBGECity[]>(`/ibge/cities/${uf}`);
    setCities(response.data);
  }, []);

  const handleLoadSyndicates = useCallback(async () => {
    if (user.role !== 'admin' || syndicates.length > 0) return;
    const response = await api.get<ISyndicateInfo[]>('/unions');
    setSyndicates(response.data);
  }, [user.role, syndicates.length]);

  const handleOpenEdit = useCallback(async () => {
    await Promise.all([handleLoadStates(), handleLoadSyndicates()]);
    if (companyInfo?.state) await handleLoadCities(companyInfo.state);
    setShowEditModal(true);
  }, [
    handleLoadStates,
    handleLoadSyndicates,
    handleLoadCities,
    companyInfo?.state,
  ]);

  const handleOpenAddCity = useCallback(async () => {
    await handleLoadStates();
    setShowAddCityModal(true);
  }, [handleLoadStates]);

  const handleOpenEditCity = useCallback(
    async (city: ICityInfo) => {
      setEditingCity(city);
      await handleLoadStates();
      if (city.state) await handleLoadCities(city.state);
      setShowEditCityModal(true);
    },
    [handleLoadStates, handleLoadCities],
  );

  const handleOpenDeleteCity = useCallback((cityId: string) => {
    setPendingCityDeleteId(cityId);
    setShowDeleteCityModal(true);
  }, []);

  const handleUpdateCompany = useCallback(
    async (values: ICompanyFormValues) => {
      setSubmitting(true);
      try {
        const payload = {
          ...values,
          syndicate_id:
            user.role === 'admin'
              ? values.syndicate_id || null
              : user.syndicate_id,
        };
        const response = await api.put<ICompanyInfo>(
          `/companies/${id}`,
          payload,
        );
        setCompanyInfo(prev => ({ ...prev, ...response.data }));
        addToast({ title: 'Empresa atualizada', body: 'Dados salvos.' });
        setShowEditModal(false);
        loadCompany();
      } catch {
        addToast({
          title: 'Erro',
          body: 'Não foi possível salvar as alterações.',
        });
      } finally {
        setSubmitting(false);
      }
    },
    [id, user, addToast, loadCompany],
  );

  const handleDeleteCompany = useCallback(async () => {
    try {
      await api.delete(`/companies/${id}`);
      addToast({ title: 'Empresa removida', body: 'A empresa foi excluída.' });
      history.push('/settings/companies');
    } catch {
      addToast({ title: 'Erro', body: 'Falha ao remover a empresa.' });
    }
  }, [id, addToast, history]);

  const handleCreateCity = useCallback(
    async (values: ICityFormValues, reset: () => void) => {
      setSubmitting(true);
      try {
        await api.post('/city', values);
        addToast({ title: 'Filial criada', body: 'Filial adicionada.' });
        reset();
        setShowAddCityModal(false);
        loadCompany();
      } catch {
        addToast({ title: 'Erro', body: 'Falha ao criar a filial.' });
      } finally {
        setSubmitting(false);
      }
    },
    [addToast, loadCompany],
  );

  const handleUpdateCity = useCallback(
    async (values: ICityFormValues) => {
      if (!editingCity) return;
      setSubmitting(true);
      try {
        await api.put(`/city/${editingCity.id}`, values);
        addToast({ title: 'Filial atualizada', body: 'Dados salvos.' });
        setShowEditCityModal(false);
        setEditingCity(null);
        loadCompany();
      } catch {
        addToast({ title: 'Erro', body: 'Falha ao atualizar a filial.' });
      } finally {
        setSubmitting(false);
      }
    },
    [editingCity, addToast, loadCompany],
  );

  const handleDeleteCity = useCallback(async () => {
    if (!pendingCityDeleteId) return;
    try {
      await api.delete(`/city/${pendingCityDeleteId}`);
      addToast({ title: 'Filial removida', body: 'A filial foi excluída.' });
      setShowDeleteCityModal(false);
      setPendingCityDeleteId(null);
      loadCompany();
    } catch {
      addToast({ title: 'Erro', body: 'Falha ao remover a filial.' });
    }
  }, [pendingCityDeleteId, addToast, loadCompany]);

  const stats = useMemo(() => {
    const cityList = companyInfo?.cities || [];
    const complaintsList = companyInfo?.complaints || [];
    return {
      cities: cityList.length,
      complaints: complaintsList.length,
      open: complaintsList.filter(c => c.status === 1 || c.status === 2).length,
      finished: complaintsList.filter(c => c.status === 3).length,
    };
  }, [companyInfo]);

  if (loading || !companyInfo) {
    return (
      <BasePage title="Empresa">
        <LoadingState />
      </BasePage>
    );
  }

  const locationLine = [companyInfo.city?.nome, companyInfo.state]
    .filter(Boolean)
    .join(' · ');

  return (
    <BasePage title="Empresa">
      <Page>
        <BackLink
          type="button"
          onClick={() => history.push('/settings/companies')}
        >
          <FiArrowLeft size={14} />
          Voltar para empresas
        </BackLink>

        <Card>
          <ProfileHeader>
            <Avatar>{initials(companyInfo.nome_fantasia)}</Avatar>
            <Identity>
              <h1>{companyInfo.nome_fantasia}</h1>
              {companyInfo.razao_social && (
                <span className="razao">{companyInfo.razao_social}</span>
              )}
              <div className="meta">
                {companyInfo.cnpj && (
                  <span>
                    <FiHash size={13} />
                    {companyInfo.cnpj}
                  </span>
                )}
                {companyInfo.syndicate?.nome_fantasia && (
                  <span>
                    <FiHome size={13} />
                    {companyInfo.syndicate.nome_fantasia}
                  </span>
                )}
                {locationLine && (
                  <span>
                    <FiMapPin size={13} />
                    {locationLine}
                  </span>
                )}
              </div>
            </Identity>
            <HeaderActions className="header-actions">
              <GhostButton type="button" onClick={handleOpenEdit}>
                <FiEdit2 size={14} />
                Editar
              </GhostButton>
              <DangerButton
                type="button"
                onClick={() => setShowDeleteModal(true)}
              >
                <FiTrash2 size={14} />
                Remover
              </DangerButton>
            </HeaderActions>
          </ProfileHeader>
        </Card>

        <StatsRow>
          <StatCard $tone="info">
            <span className="icon">
              <FiLayers size={20} />
            </span>
            <div className="body">
              <span className="label">Filiais</span>
              <span className="value">{stats.cities}</span>
            </div>
          </StatCard>
          <StatCard $tone="brand">
            <span className="icon">
              <FiAlertCircle size={20} />
            </span>
            <div className="body">
              <span className="label">Denúncias totais</span>
              <span className="value">{stats.complaints}</span>
            </div>
          </StatCard>
          <StatCard $tone="warning">
            <span className="icon">
              <FiClock size={20} />
            </span>
            <div className="body">
              <span className="label">Em aberto</span>
              <span className="value">{stats.open}</span>
            </div>
          </StatCard>
          <StatCard $tone="success">
            <span className="icon">
              <FiCheckCircle size={20} />
            </span>
            <div className="body">
              <span className="label">Finalizadas</span>
              <span className="value">{stats.finished}</span>
            </div>
          </StatCard>
        </StatsRow>

        <Card>
          <Tabs>
            <TabButton
              type="button"
              $active={activeTab === 'overview'}
              onClick={() => setActiveTab('overview')}
            >
              <FiBriefcase size={14} />
              Visão geral
            </TabButton>
            <TabButton
              type="button"
              $active={activeTab === 'complaints'}
              onClick={() => setActiveTab('complaints')}
            >
              <FiAlertCircle size={14} />
              Denúncias
              <span className="count">{stats.complaints}</span>
            </TabButton>
            <TabButton
              type="button"
              $active={activeTab === 'cities'}
              onClick={() => setActiveTab('cities')}
            >
              <FiLayers size={14} />
              Filiais
              <span className="count">{stats.cities}</span>
            </TabButton>
          </Tabs>

          {activeTab === 'overview' && (
            <>
              <SectionHeader>
                <FiBriefcase size={14} />
                <h3>Identificação</h3>
              </SectionHeader>
              <SectionBody>
                <InfoGrid>
                  <Field>
                    <span className="label">Nome fantasia</span>
                    {renderValue(companyInfo.nome_fantasia)}
                  </Field>
                  <Field>
                    <span className="label">Razão social</span>
                    {renderValue(companyInfo.razao_social)}
                  </Field>
                  <Field>
                    <span className="label">CNPJ</span>
                    {renderValue(companyInfo.cnpj)}
                  </Field>
                  <Field $span={3}>
                    <span className="label">Sindicato</span>
                    {renderValue(companyInfo.syndicate?.nome_fantasia)}
                  </Field>
                </InfoGrid>
              </SectionBody>

              <SectionHeader>
                <FiMapPin size={14} />
                <h3>Endereço</h3>
              </SectionHeader>
              <SectionBody>
                <InfoGrid>
                  <Field $span={2}>
                    <span className="label">Endereço</span>
                    {renderValue(companyInfo.address)}
                  </Field>
                  <Field>
                    <span className="label">Complemento</span>
                    {renderValue(companyInfo.address2)}
                  </Field>
                  <Field>
                    <span className="label">CEP</span>
                    {renderValue(companyInfo.postal_code)}
                  </Field>
                  <Field>
                    <span className="label">Município</span>
                    {renderValue(companyInfo.city?.nome)}
                  </Field>
                  <Field>
                    <span className="label">UF</span>
                    {renderValue(companyInfo.state)}
                  </Field>
                </InfoGrid>
              </SectionBody>
            </>
          )}

          {activeTab === 'complaints' && (
            <>
              <SectionHeader>
                <FiAlertCircle size={14} />
                <h3>Denúncias vinculadas</h3>
              </SectionHeader>
              {(companyInfo.complaints || []).length === 0 ? (
                <EmptyState>
                  <span className="icon">
                    <FiInbox size={20} />
                  </span>
                  <span className="title">Nenhuma denúncia registrada</span>
                  <span className="desc">
                    Quando trabalhadores abrirem denúncias contra esta empresa,
                    elas aparecerão aqui.
                  </span>
                </EmptyState>
              ) : (
                <ComplaintList>
                  {(companyInfo.complaints || [])
                    .slice()
                    .sort((a, b) => {
                      const ta = a.created_at
                        ? new Date(a.created_at).getTime()
                        : 0;
                      const tb = b.created_at
                        ? new Date(b.created_at).getTime()
                        : 0;
                      return tb - ta;
                    })
                    .map(complaint => {
                      const status = complaint.status || 1;
                      const tone = STATUS_TONE[status] || 'neutral';
                      return (
                        <ComplaintItem
                          key={complaint.id}
                          onClick={() => history.push(`/complaints/${complaint.id}`)}
                        >
                          <div className="protocol">
                            <span className="protocol-num">
                              {complaint.protocol || '—'}
                            </span>
                            <span className="meta">
                              {complaint.created_at && (
                                <span>
                                  <FiClock size={11} />
                                  {format(
                                    new Date(complaint.created_at),
                                    "dd/MM/yyyy 'às' HH:mm",
                                  )}
                                </span>
                              )}
                            </span>
                          </div>
                          <StatusChip $tone={tone}>
                            <span className="dot" />
                            {STATUS_LABEL[status] || '—'}
                          </StatusChip>
                          <SmallAction
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              history.push(`/complaints/${complaint.id}`);
                            }}
                          >
                            <FiEye size={12} />
                            Abrir
                          </SmallAction>
                          <FiChevronRight size={14} color="#cbd5e1" />
                        </ComplaintItem>
                      );
                    })}
                </ComplaintList>
              )}
            </>
          )}

          {activeTab === 'cities' && (
            <>
              <SectionHeader>
                <FiLayers size={14} />
                <h3>Filiais</h3>
                <div className="right">
                  <PrimaryButton type="button" onClick={handleOpenAddCity}>
                    <FiPlus size={14} />
                    Nova filial
                  </PrimaryButton>
                </div>
              </SectionHeader>
              {(companyInfo.cities || []).length === 0 ? (
                <EmptyState>
                  <span className="icon">
                    <FiLayers size={20} />
                  </span>
                  <span className="title">Nenhuma filial cadastrada</span>
                  <span className="desc">
                    Cadastre filiais para identificar locais de trabalho
                    adicionais desta empresa.
                  </span>
                </EmptyState>
              ) : (
                <CityList>
                  {(companyInfo.cities || []).map(city => (
                    <CityItem key={city.id}>
                      <span className="icon">
                        <FiHome size={16} />
                      </span>
                      <div className="info">
                        <span className="name">{city.name}</span>
                        <div className="meta">
                          {city.city?.nome && (
                            <span>
                              <FiMapPin size={11} />
                              {city.city.nome}
                              {city.state ? ` · ${city.state}` : ''}
                            </span>
                          )}
                          {city.postal_code && (
                            <span>
                              <FiHash size={11} />
                              {city.postal_code}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="actions">
                        <SmallAction
                          type="button"
                          onClick={() => handleOpenEditCity(city)}
                        >
                          <FiEdit2 size={12} />
                          Editar
                        </SmallAction>
                        <SmallAction
                          type="button"
                          $danger
                          onClick={() => handleOpenDeleteCity(city.id)}
                          aria-label="Remover filial"
                        >
                          <FiTrash2 size={12} />
                        </SmallAction>
                      </div>
                    </CityItem>
                  ))}
                </CityList>
              )}
            </>
          )}
        </Card>
      </Page>

      {/* Edit company modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        size="lg"
      >
        <Formik<ICompanyFormValues>
          enableReinitialize
          initialValues={{
            nome_fantasia: companyInfo.nome_fantasia || '',
            razao_social: companyInfo.razao_social || '',
            cnpj: companyInfo.cnpj || '',
            syndicate_id: companyInfo.syndicate_id || '',
            postal_code: companyInfo.postal_code || '',
            address: companyInfo.address || '',
            address2: companyInfo.address2 || '',
            state: companyInfo.state || '',
            city_id: companyInfo.city_id || '',
          }}
          validationSchema={companySchema}
          onSubmit={handleUpdateCompany}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => (
            <>
              <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
                  Editar empresa
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <ModalForm onSubmit={handleSubmit}>
                  <SectionLabel>
                    <FiBriefcase size={11} />
                    Identificação
                  </SectionLabel>

                  <FieldGroup $cols={user.role === 'admin' ? 2 : 1}>
                    <FormField>
                      <label htmlFor="nome_fantasia">
                        Nome fantasia *
                        <input
                          id="nome_fantasia"
                          name="nome_fantasia"
                          value={values.nome_fantasia}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </label>
                      {touched.nome_fantasia && errors.nome_fantasia && (
                        <span className="error">{errors.nome_fantasia}</span>
                      )}
                    </FormField>

                    {user.role === 'admin' && (
                      <FormField>
                        <label htmlFor="syndicate_id">
                          Sindicato
                          <select
                            id="syndicate_id"
                            name="syndicate_id"
                            value={values.syndicate_id}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <option value="">Selecione um sindicato</option>
                            {syndicates.map(s => (
                              <option key={s.id} value={s.id}>
                                {s.nome_fantasia}
                              </option>
                            ))}
                          </select>
                        </label>
                      </FormField>
                    )}
                  </FieldGroup>

                  <FieldGroup>
                    <FormField>
                      <label htmlFor="razao_social">
                        Razão social
                        <input
                          id="razao_social"
                          name="razao_social"
                          value={values.razao_social}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </label>
                    </FormField>
                    <FormField>
                      <label htmlFor="cnpj">
                        CNPJ
                        <input
                          id="cnpj"
                          name="cnpj"
                          placeholder="00.000.000/0000-00"
                          maxLength={18}
                          value={values.cnpj}
                          onChange={e => setFieldValue('cnpj', cnpjMask(e.target.value))}
                          onBlur={handleBlur}
                        />
                      </label>
                    </FormField>
                  </FieldGroup>

                  <SectionLabel>
                    <FiMapPin size={11} />
                    Endereço
                  </SectionLabel>

                  <FieldGroup $cols={3}>
                    <FormField>
                      <label htmlFor="postal_code">
                        CEP
                        <input
                          id="postal_code"
                          name="postal_code"
                          placeholder="00000-000"
                          maxLength={9}
                          value={values.postal_code}
                          onChange={e => setFieldValue('postal_code', cepMask(e.target.value))}
                          onBlur={handleBlur}
                        />
                      </label>
                    </FormField>
                    <FormField>
                      <label htmlFor="state">
                        Estado
                        <select
                          id="state"
                          name="state"
                          value={values.state}
                          onChange={e => {
                            setFieldValue('state', e.target.value);
                            setFieldValue('city_id', '');
                            handleLoadCities(e.target.value);
                          }}
                          onBlur={handleBlur}
                        >
                          <option value="">UF</option>
                          {states.map(s => (
                            <option key={s.uf} value={s.uf}>
                              {s.uf}
                            </option>
                          ))}
                        </select>
                      </label>
                    </FormField>
                    <FormField>
                      <label htmlFor="city_id">
                        Município
                        <select
                          id="city_id"
                          name="city_id"
                          value={values.city_id}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={!values.state}
                        >
                          <option value="">
                            {values.state
                              ? 'Selecione o município'
                              : 'Selecione o estado primeiro'}
                          </option>
                          {cities.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.nome}
                            </option>
                          ))}
                        </select>
                      </label>
                    </FormField>
                  </FieldGroup>

                  <FieldGroup>
                    <FormField>
                      <label htmlFor="address">
                        Endereço
                        <input
                          id="address"
                          name="address"
                          value={values.address}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </label>
                    </FormField>
                    <FormField>
                      <label htmlFor="address2">
                        Complemento
                        <input
                          id="address2"
                          name="address2"
                          value={values.address2}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </label>
                    </FormField>
                  </FieldGroup>
                </ModalForm>
              </Modal.Body>
              <Modal.Footer>
                <GhostButton
                  type="button"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancelar
                </GhostButton>
                <PrimaryButton
                  type="button"
                  onClick={() => handleSubmit()}
                  disabled={submitting}
                >
                  <FiCheckCircle size={14} />
                  {submitting ? 'Salvando...' : 'Salvar alterações'}
                </PrimaryButton>
              </Modal.Footer>
            </>
          )}
        </Formik>
      </Modal>

      {/* Delete company modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
            Remover empresa
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ModalNote>
            Tem certeza que deseja remover
            {' '}
            <strong>{companyInfo.nome_fantasia}</strong>
            ? Essa ação não poderá ser desfeita.
          </ModalNote>
        </Modal.Body>
        <Modal.Footer>
          <GhostButton type="button" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </GhostButton>
          <DangerButton type="button" onClick={handleDeleteCompany}>
            <FiTrash2 size={14} />
            Remover empresa
          </DangerButton>
        </Modal.Footer>
      </Modal>

      {/* Add city modal */}
      <Modal
        show={showAddCityModal}
        onHide={() => setShowAddCityModal(false)}
        centered
      >
        <Formik<ICityFormValues>
          initialValues={cityFormDefaults(id)}
          onSubmit={(values, helpers) => handleCreateCity(values, () => helpers.resetForm())}
        >
          {({
            values,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => (
            <>
              <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
                  Nova filial
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <ModalForm onSubmit={handleSubmit}>
                  <FormField>
                    <label htmlFor="city-name">
                      Nome da filial *
                      <input
                        id="city-name"
                        name="name"
                        placeholder="Ex: Galpão Norte"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </label>
                  </FormField>

                  <FieldGroup $cols={3}>
                    <FormField>
                      <label htmlFor="city-cep">
                        CEP
                        <input
                          id="city-cep"
                          name="postal_code"
                          placeholder="00000-000"
                          maxLength={9}
                          value={values.postal_code}
                          onChange={e => setFieldValue('postal_code', cepMask(e.target.value))}
                          onBlur={handleBlur}
                        />
                      </label>
                    </FormField>
                    <FormField>
                      <label htmlFor="city-state">
                        UF
                        <select
                          id="city-state"
                          name="state"
                          value={values.state}
                          onChange={e => {
                            setFieldValue('state', e.target.value);
                            setFieldValue('city_id', '');
                            handleLoadCities(e.target.value);
                          }}
                          onBlur={handleBlur}
                        >
                          <option value="">UF</option>
                          {states.map(s => (
                            <option key={s.uf} value={s.uf}>
                              {s.uf}
                            </option>
                          ))}
                        </select>
                      </label>
                    </FormField>
                    <FormField>
                      <label htmlFor="city-city">
                        Município
                        <select
                          id="city-city"
                          name="city_id"
                          value={values.city_id}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={!values.state}
                        >
                          <option value="">
                            {values.state ? 'Selecione' : 'Escolha a UF'}
                          </option>
                          {cities.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.nome}
                            </option>
                          ))}
                        </select>
                      </label>
                    </FormField>
                  </FieldGroup>

                  <FieldGroup>
                    <FormField>
                      <label htmlFor="city-address">
                        Endereço
                        <input
                          id="city-address"
                          name="address"
                          value={values.address}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </label>
                    </FormField>
                    <FormField>
                      <label htmlFor="city-address2">
                        Complemento
                        <input
                          id="city-address2"
                          name="address2"
                          value={values.address2}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </label>
                    </FormField>
                  </FieldGroup>
                </ModalForm>
              </Modal.Body>
              <Modal.Footer>
                <GhostButton
                  type="button"
                  onClick={() => setShowAddCityModal(false)}
                >
                  Cancelar
                </GhostButton>
                <PrimaryButton
                  type="button"
                  onClick={() => handleSubmit()}
                  disabled={submitting}
                >
                  <FiPlus size={14} />
                  {submitting ? 'Salvando...' : 'Adicionar filial'}
                </PrimaryButton>
              </Modal.Footer>
            </>
          )}
        </Formik>
      </Modal>

      {/* Edit city modal */}
      <Modal
        show={showEditCityModal}
        onHide={() => {
          setShowEditCityModal(false);
          setEditingCity(null);
        }}
        centered
      >
        <Formik<ICityFormValues>
          enableReinitialize
          initialValues={{
            name: editingCity?.name || '',
            postal_code: editingCity?.postal_code || '',
            address: editingCity?.address || '',
            address2: editingCity?.address2 || '',
            state: editingCity?.state || '',
            city_id: editingCity?.city_id || '',
            company_id: id,
          }}
          onSubmit={handleUpdateCity}
        >
          {({
            values,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => (
            <>
              <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
                  Editar filial
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <ModalForm onSubmit={handleSubmit}>
                  <FormField>
                    <label htmlFor="ec-name">
                      Nome da filial *
                      <input
                        id="ec-name"
                        name="name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </label>
                  </FormField>

                  <FieldGroup $cols={3}>
                    <FormField>
                      <label htmlFor="ec-cep">
                        CEP
                        <input
                          id="ec-cep"
                          name="postal_code"
                          placeholder="00000-000"
                          maxLength={9}
                          value={values.postal_code}
                          onChange={e => setFieldValue('postal_code', cepMask(e.target.value))}
                          onBlur={handleBlur}
                        />
                      </label>
                    </FormField>
                    <FormField>
                      <label htmlFor="ec-state">
                        UF
                        <select
                          id="ec-state"
                          name="state"
                          value={values.state}
                          onChange={e => {
                            setFieldValue('state', e.target.value);
                            setFieldValue('city_id', '');
                            handleLoadCities(e.target.value);
                          }}
                          onBlur={handleBlur}
                        >
                          <option value="">UF</option>
                          {states.map(s => (
                            <option key={s.uf} value={s.uf}>
                              {s.uf}
                            </option>
                          ))}
                        </select>
                      </label>
                    </FormField>
                    <FormField>
                      <label htmlFor="ec-city">
                        Município
                        <select
                          id="ec-city"
                          name="city_id"
                          value={values.city_id}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={!values.state}
                        >
                          <option value="">
                            {values.state ? 'Selecione' : 'Escolha a UF'}
                          </option>
                          {cities.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.nome}
                            </option>
                          ))}
                        </select>
                      </label>
                    </FormField>
                  </FieldGroup>

                  <FieldGroup>
                    <FormField>
                      <label htmlFor="ec-address">
                        Endereço
                        <input
                          id="ec-address"
                          name="address"
                          value={values.address}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </label>
                    </FormField>
                    <FormField>
                      <label htmlFor="ec-address2">
                        Complemento
                        <input
                          id="ec-address2"
                          name="address2"
                          value={values.address2}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </label>
                    </FormField>
                  </FieldGroup>
                </ModalForm>
              </Modal.Body>
              <Modal.Footer>
                <GhostButton
                  type="button"
                  onClick={() => {
                    setShowEditCityModal(false);
                    setEditingCity(null);
                  }}
                >
                  Cancelar
                </GhostButton>
                <PrimaryButton
                  type="button"
                  onClick={() => handleSubmit()}
                  disabled={submitting}
                >
                  <FiCheckCircle size={14} />
                  {submitting ? 'Salvando...' : 'Salvar'}
                </PrimaryButton>
              </Modal.Footer>
            </>
          )}
        </Formik>
      </Modal>

      {/* Delete city modal */}
      <Modal
        show={showDeleteCityModal}
        onHide={() => {
          setShowDeleteCityModal(false);
          setPendingCityDeleteId(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
            Remover filial
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ModalNote>
            Tem certeza que deseja remover esta filial? Essa ação não poderá ser
            desfeita.
          </ModalNote>
        </Modal.Body>
        <Modal.Footer>
          <GhostButton
            type="button"
            onClick={() => {
              setShowDeleteCityModal(false);
              setPendingCityDeleteId(null);
            }}
          >
            Cancelar
          </GhostButton>
          <DangerButton type="button" onClick={handleDeleteCity}>
            <FiTrash2 size={14} />
            Remover
          </DangerButton>
        </Modal.Footer>
      </Modal>
    </BasePage>
  );
};

export default ShowCompany;
